import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { DataGridColumnConfig, DataGridFilterType } from "components/DataGrid/types";
import { capitalize, isEmpty } from "lodash";
import { getAriaSortAttribute } from "./utils";
import { Button } from "components/Button";
import { DataGridFilterHeaders } from "./DataGridFilterHeader";
import { Form } from "react-bootstrap";
import useMediaQuery from "components/utils/useMediaQuery";
import { useFilter } from "./useFilter";

export const DataGridHeader: React.FC<{
    columns: DataGridColumnConfig[];
    hasActionsColumn?: boolean;
    stickyActionsColumn?: boolean;
    canSelectRows?: boolean;
    isFilterOpen?: boolean;
    isSortOpen?: boolean;
    isFilterEmpty: boolean;
    filter?: DataGridFilterType;
    sortBy?: string;
    sortAsc?: boolean;
    allRowsSelected?: boolean;
    isFilterChanged: boolean;
    totalRecords?: number;
    totalSelectedRecords?: number;
    SelectedActionsRowComponent?: JSX.Element | null;
    onSortChange: (key: string) => void;
    onFilterClear: () => void;
    onSortClear: () => void;
    toggleFilter: (status?: boolean) => void;
    toggleSort: (status?: boolean) => void;
    handleSelectAllRows?: (forceUnselectAll?: boolean) => void;
    getFilterValue: ReturnType<typeof useFilter>["getFilterValue"];
    onFieldClean: (key: string) => void;
    onFilterSubmit: (filterValue?: DataGridFilterType) => void;
    onFilterInputFocus?: (key: string) => void;
}> = ({
    columns,
    hasActionsColumn = true,
    stickyActionsColumn = true,
    canSelectRows,
    isFilterOpen,
    isSortOpen,
    isFilterEmpty,
    toggleFilter,
    toggleSort,
    filter = {},
    sortBy,
    sortAsc,
    totalRecords = 0,
    totalSelectedRecords,
    SelectedActionsRowComponent,
    onSortChange,
    onFilterClear,
    onSortClear,
    handleSelectAllRows,
    allRowsSelected,
    getFilterValue,
    onFilterSubmit,
    onFieldClean,
    onFilterInputFocus,
}) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    if (isEmpty(columns)) {
        return null;
    }

    return (
        <>
            {onFilterInputFocus && (
                <DataGridFilterHeaders
                    isFilterOpen={isFilterOpen}
                    isSortOpen={isSortOpen}
                    toggleFilter={toggleFilter}
                    toggleSort={toggleSort}
                    totalRecords={totalRecords}
                    totalSelectedRecords={totalSelectedRecords}
                    SelectedActionsRowComponent={SelectedActionsRowComponent}
                    isFilterEmpty={isFilterEmpty}
                    canSelectRows={canSelectRows}
                    columns={columns}
                    filter={filter}
                    sortBy={sortBy}
                    sortAsc={sortAsc}
                    getFilterValue={getFilterValue}
                    onFilterClear={onFilterClear}
                    onSortClear={onSortClear}
                    onFilterSubmit={onFilterSubmit}
                    onFieldClean={onFieldClean}
                    handleSelectAllRows={handleSelectAllRows}
                    onFilterInputFocus={onFilterInputFocus}
                    onSortChange={onSortChange}
                />
            )}
            {totalRecords > 0 && isMobile && canSelectRows && (
                <tr className="d-table-row">
                    <td className="d-flex align-items-center gap-2 py-3 select-all border border-start-0 border-end-0 border-top-0">
                        <Form.Check className="d-inline">
                            <Form.Check.Input
                                checked={allRowsSelected}
                                onChange={() => handleSelectAllRows && handleSelectAllRows()}
                                className="m-0"
                                aria-label="Select all rows"
                            />
                        </Form.Check>

                        <span>Select all</span>
                    </td>
                </tr>
            )}
            <tr className="data-grid-header">
                {canSelectRows && (
                    <th className="col-auto d-flex d-lg-table-cell py-3 select-column align-middle border border-start-0 border-top-0 rounded-0">
                        <Form.Check className="d-inline">
                            <Form.Check.Input
                                checked={allRowsSelected}
                                onChange={() => handleSelectAllRows && handleSelectAllRows()}
                                className="m-0"
                                aria-label="Select all rows"
                            />
                        </Form.Check>
                    </th>
                )}
                {columns.map((column, index) => {
                    const columnName = (column.name ?? "").split(" ").map(capitalize).join(" ");

                    let sortTitle;
                    switch (column.sort) {
                        case "ASC":
                            sortTitle = `Sort By ${columnName} Descending`;
                            break;
                        default:
                            sortTitle = `Sort By ${columnName} Ascending`;
                            break;
                    }

                    let sortIcon;
                    switch (column.sort) {
                        case "ASC":
                            sortIcon = <FontAwesomeIcon icon="arrow-down-short-wide" size="sm" />;
                            break;
                        case "DESC":
                            sortIcon = <FontAwesomeIcon icon="arrow-up-short-wide" size="sm" />;
                            break;
                        default:
                            sortIcon = <FontAwesomeIcon icon="arrow-down-arrow-up" size="sm" />;
                            break;
                    }

                    return (
                        <th
                            key={index}
                            scope="col"
                            className={cn(column.className, "border border-end-0 border-top-0 rounded-0 py-1 ", {
                                "border-start-0": index === 0,
                                sorted: column.sort,
                                hidden: column.hideHeader,
                            })}
                            aria-sort={getAriaSortAttribute(column)}
                        >
                            {!column.hideHeader && (
                                <button
                                    onClick={() => onSortChange(column.key)}
                                    title={sortTitle}
                                    aria-label={sortTitle}
                                    className={cn("data-grid-column-sort w-100 d-flex flex-row align-items-center gap-2 p-2")}
                                >
                                    <div className="my-auto text-capitalize text-nowrap d-flex gap-2 fw-bold">{column.name}</div>
                                    {sortIcon}
                                </button>
                            )}
                        </th>
                    );
                })}

                {hasActionsColumn && (
                    <th
                        style={{ width: "7rem" }}
                        className={cn("action-column text-center border border-top-0 border-end-0 rounded-0", {
                            "position-sticky top-0 end-0": stickyActionsColumn,
                        })}
                    >
                        <span className="visually-hidden">Actions</span>
                        {toggleFilter && !isFilterOpen && (
                            <Button
                                className="filter-toggler d-flex align-items-center mx-auto"
                                variant="link"
                                title="Filter"
                                aria-label="Filter"
                                onClick={() => toggleFilter()}
                            >
                                <FontAwesomeIcon className="pe-2 fs-6" icon={["fal", "magnifying-glass"]} />
                                <FontAwesomeIcon className="ps-1 fs-5" icon={["fal", "angle-down"]} />
                            </Button>
                        )}
                    </th>
                )}
            </tr>
        </>
    );
};
